import {pulseUserAtom} from 'atoms/auth';
import {
  applyGrowthbookTempExperimentOverrideAtom,
  applyGrowthbookTempOverrideAtom,
  getActiveTempExperimentOverridesForUser,
  getActiveTempOverridesForUser,
  getGrowthbookOverrideUserKey,
  growthbookTempExperimentOverridesAtom,
  growthbookTempOverridesAtom,
  parseGrowthbookOverrideParams,
  refreshGrowthbookTempOverridesAtom,
} from 'atoms/growthbook';
import {useAtomValue, useSetAtom} from 'jotai';
import {useEffect, useRef} from 'react';
import {useSearchParams} from 'react-router';

export function useGrowthbookQueryOverrides() {
  const [searchParams, setSearchParams] = useSearchParams();
  const applyTempOverride = useSetAtom(applyGrowthbookTempOverrideAtom);
  const applyTempExperimentOverride = useSetAtom(applyGrowthbookTempExperimentOverrideAtom);
  const refreshTempOverrides = useSetAtom(refreshGrowthbookTempOverridesAtom);
  const allTempOverrides = useAtomValue(growthbookTempOverridesAtom);
  const allTempExperimentOverrides = useAtomValue(growthbookTempExperimentOverridesAtom);
  const currentUser = useAtomValue(pulseUserAtom);
  const userKey = getGrowthbookOverrideUserKey(currentUser);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const parsed = parseGrowthbookOverrideParams(searchParams);
    if (parsed.overrides.length === 0 && parsed.experimentOverrides.length === 0) {
      return;
    }

    for (const override of parsed.overrides) {
      applyTempOverride({
        featureKey: override.featureKey,
        value: override.value,
        ttlMinutes: parsed.ttlMinutes,
      });
    }

    for (const override of parsed.experimentOverrides) {
      applyTempExperimentOverride({
        experimentKey: override.experimentKey,
        value: override.value,
        ttlMinutes: parsed.ttlMinutes,
      });
    }

    const nextParams = new URLSearchParams(searchParams);
    for (const key of parsed.matchedParamKeys) {
      nextParams.delete(key);
    }
    if (parsed.ttlParamPresent) {
      nextParams.delete('ttl');
    }
    setSearchParams(nextParams, {replace: true});
  }, [applyTempOverride, applyTempExperimentOverride, currentUser, searchParams, setSearchParams]);

  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }

    const activeOverrides = getActiveTempOverridesForUser(allTempOverrides, userKey);
    const activeExperimentOverrides = getActiveTempExperimentOverridesForUser(
      allTempExperimentOverrides,
      userKey
    );
    const expiryTimes = [
      ...Object.values(activeOverrides).map((override) => override.expiresAt),
      ...Object.values(activeExperimentOverrides)
        .filter((o): o is NonNullable<typeof o> => o != null)
        .map((override) => override.expiresAt),
    ];
    if (expiryTimes.length === 0) {
      return;
    }

    const soonestExpiry = Math.min(...expiryTimes);
    const delay = Math.max(0, soonestExpiry - Date.now());

    expiryTimerRef.current = setTimeout(() => {
      refreshTempOverrides();
    }, delay);

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [allTempOverrides, allTempExperimentOverrides, refreshTempOverrides, userKey]);
}
